import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import { animeNews } from "@/mocks/news";
import NewsCard from "@/components/base/NewsCard";
import EmptyState from "@/components/base/EmptyState";
import Pagination from "@/components/base/Pagination";
import Breadcrumb from "@/components/base/Breadcrumb";

const PAGE_SIZE = 6;
const categories = ["全部", "新闻", "专题", "评论"] as const;

export default function News() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [sort, setSort] = useState<"latest" | "popular">("latest");
  const [page, setPage] = useState(1);
  const [inputValue, setInputValue] = useState(searchParams.get("q") ?? "");

  useSeo({
    title: "最新萝莉动漫资讯 - 萝莉动漫主题站",
    description:
      "萝莉动漫资讯栏目汇集最新新番动态、角色情报与深度专题评论，支持分类筛选、关键词搜索与热度排序，及时掌握萝莉文化相关内容。",
    keywords: "萝莉资讯,动漫资讯,新番动态,动漫专题,二次元资讯"
  });

  const keyword = searchParams.get("q") ?? "";
  const category = searchParams.get("cat") ?? "全部";

  const update = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next, { replace: true });
    setPage(1);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    update("q", inputValue.trim());
  };

  const filtered = useMemo(() => {
    const q = keyword.trim().toLowerCase();
    const list = animeNews.filter((n) => {
      const matchKeyword =
        !q || n.title.toLowerCase().includes(q) || n.summary.toLowerCase().includes(q);
      const matchCat = category === "全部" || n.category === category;
      return matchKeyword && matchCat;
    });
    return list.sort((a, b) =>
      sort === "latest" ? b.date.localeCompare(a.date) : b.popularity - a.popularity
    );
  }, [keyword, category, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const current = Math.min(page, totalPages);
  const pageItems = filtered.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  const hotNews = [...animeNews].sort((a, b) => b.popularity - a.popularity).slice(0, 5);

  const handleReset = () => {
    setSearchParams(new URLSearchParams(), { replace: true });
    setInputValue("");
    setPage(1);
  };

  return (
    <div className="mx-auto max-w-[1280px] px-4 py-6 md:px-6 md:py-8">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "动漫资讯" }]} />

      <div className="mt-4 mb-6">
        <h1 className="text-2xl text-foreground-950 md:text-3xl">动漫资讯</h1>
        <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600">
          聚合最新的萝莉动漫资讯、深度专题与文化评论，支持关键词精确搜索与分类筛选，帮你不遗漏任何一条值得关注的消息。
        </p>
      </div>

      <div className="rounded-lg border border-background-200 bg-background-50 p-4">
        <form onSubmit={handleSearch} className="flex flex-col gap-3 sm:flex-row">
          <div className="flex flex-1 items-center gap-2 rounded-md border border-background-300 bg-background-100 px-3 py-2.5 transition-colors focus-within:border-primary-300">
            <i className="ri-search-line text-foreground-500"></i>
            <input
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder="搜索资讯标题或关键词"
              className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none"
            />
          </div>
          <button
            type="submit"
            className="rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer dark:text-foreground-950"
          >
            搜索
          </button>
        </form>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <span className="text-sm font-medium text-foreground-700">分类</span>
          <div className="flex flex-wrap gap-2">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => update("cat", c === "全部" ? "" : c)}
                className={`rounded-full border px-3 py-1 text-xs transition-colors cursor-pointer ${
                  category === c
                    ? "border-primary-300 bg-primary-100 text-primary-700"
                    : "border-background-300 bg-background-50 text-foreground-600 hover:border-primary-200"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-[1fr_300px]">
        <div>
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-foreground-600">
              {keyword && (
                <span className="mr-2">
                  关键词「<span className="text-primary-600">{keyword}</span>」 ·
                </span>
              )}
              共 <span className="font-medium text-foreground-900">{filtered.length}</span> 篇文章
            </p>
            <div className="flex items-center gap-1 rounded-full border border-background-300 bg-background-50 p-1">
              <button
                onClick={() => setSort("latest")}
                className={`rounded-full px-4 py-1.5 text-sm transition-colors cursor-pointer ${
                  sort === "latest"
                    ? "bg-primary-500 text-background-50 dark:text-foreground-950"
                    : "text-foreground-700 hover:text-primary-600"
                }`}
              >
                最新发布
              </button>
              <button
                onClick={() => setSort("popular")}
                className={`rounded-full px-4 py-1.5 text-sm transition-colors cursor-pointer ${
                  sort === "popular"
                    ? "bg-primary-500 text-background-50 dark:text-foreground-950"
                    : "text-foreground-700 hover:text-primary-600"
                }`}
              >
                热门程度
              </button>
            </div>
          </div>

          {pageItems.length > 0 ? (
            <div className="space-y-4">
              {pageItems.map((news) => (
                <NewsCard key={news.id} news={news} />
              ))}
            </div>
          ) : (
            <div className="space-y-6">
              <EmptyState
                icon="ri-newspaper-line"
                title="暂无相关资讯"
                desc="没有找到匹配的文章，试试其他关键词或重置筛选条件。"
                actionText="重置筛选"
                onAction={handleReset}
              />
              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h3 className="mb-3 flex items-center gap-2 font-heading text-base text-foreground-950">
                  <i className="ri-fire-line text-accent-500"></i>
                  猜你想看
                </h3>
                <div className="space-y-1">
                  {hotNews.slice(0, 3).map((n) => (
                    <NewsCard key={n.id} news={n} variant="compact" />
                  ))}
                </div>
              </div>
            </div>
          )}

          {pageItems.length > 0 && (
            <div className="mt-8">
              <Pagination current={current} total={totalPages} onChange={setPage} />
            </div>
          )}
        </div>

        <aside className="space-y-6">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h3 className="mb-4 flex items-center gap-2 font-heading text-base text-foreground-950">
              <i className="ri-fire-line text-accent-500"></i>
              热门文章
            </h3>
            <div className="space-y-1">
              {hotNews.map((n) => (
                <NewsCard key={n.id} news={n} variant="compact" />
              ))}
            </div>
          </div>
          <div className="rounded-lg border border-secondary-200 bg-secondary-50 p-5">
            <h3 className="mb-2 flex items-center gap-2 font-heading text-base text-foreground-950">
              <i className="ri-lightbulb-line text-secondary-600"></i>
              投稿须知
            </h3>
            <p className="text-sm leading-relaxed text-foreground-700">
              欢迎向本站投稿原创的角色分析与文化专题文章。内容需为原创、观点友善，审核通过后将署名发布。
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
}